<template>
  <b-spinner v-if="!isPrintersLoaded || !loaded"></b-spinner>
  <div class="table table--popup choose-printer" v-else>
    <div v-if="files.length > 1" class="choose-printer__file">
      <h3 class="text--500" v-if="action==='print'">{{files.length}} print jobs</h3>
      <h3 class="text--500" v-else>{{files.length}} models</h3>
    </div>
    <div v-else class="choose-printer__file" v-for="(file, fileIndex) in files" :key="fileIndex">
      <model-preview :iconImg="file.picturePreview"></model-preview>
      <h3 class="text--500">{{fileNameCut(file.filename, 25, false)}}</h3>
      <job-cost :cost="file.cost"></job-cost>
      <span v-for="(filament, fmIndex) in file.filaments" :key="fmIndex">
        <job-filament :filament="filament.weight"></job-filament>
        &nbsp;<span>{{filament.material}}</span>
      </span>
      <human-duration :seconds="file.printingDuration" default-value=""></human-duration>
    </div>
    <div class="choose-printer__scroll">
      <b-table :fields="fields" class="table--printers-mini" :items="printersList" primary-key="printer_id">
        <template v-slot:cell(printer)="data">
          <div class="cell-printer-name">
            <printer-status :printer="data.item"></printer-status>
            <span class="printer">{{calculatePrinterName(data.item)}}</span>
            <span class="printer printer--model">{{data.item.description}}</span>
            <printer-inline-note :note="data.item.notes" v-if="canPrint(data.item.printer_ownership_id)" :icon-style="{'top': '-4px'}"></printer-inline-note>
          </div>
        </template>
        <template v-slot:cell(filament)="data">
          <printer-filament-labels  v-if="data.item.filament_tracking==='t'" :printer="data.item"></printer-filament-labels>
        </template>
        <template v-slot:cell(action1)="data">
          <b-button v-if="action === 'copy' && data.item.printer_id != currentPrinterId && jobsData[0].job_status_id == 11"
                    :disabled="processing || isDisabledQueue(data.item.flags)"
                    variant="-secondary"
                    size="-small"
                    @click="handleMove(data.item.printer_id)">
            {{$t('printers.modal.choosePrinter.move')}}
          </b-button>
          <b-button v-if="action === 'move'"
                    :disabled="processing || isDisabledQueue(data.item.flags)"
                    variant="-secondary"
                    size="-small"
                    @click="handleMove(data.item.printer_id)">
            {{$t('printers.modal.choosePrinter.move')}}
          </b-button>
          <b-button v-if="action === 'print' && canStartPrint(data.item)"
                    :disabled="processing || isDisabledPrint(data.item)"
                    variant="-secondary"
                    size="-small"
                    @click="handleStartPrint(data.item.printer_id)">
            {{$t('printers.modal.choosePrinter.print')}}
          </b-button>
          <b-button v-if="action === 'restart' && canRestartPrint(data.item)"
                    :disabled="processing || isDisabledPrint(data.item)"
                    variant="-secondary"
                    size="-small"
                    @click="handleRestart(data.item.printer_id, false)">
            {{$t('printers.modal.choosePrinter.print')}}
          </b-button>
        </template>
        <template v-slot:cell(action2)="data">
          <b-button v-if="action === 'copy'"
                    :disabled="processing || isDisabledQueue(data.item.flags)"
                    variant="-primary"
                    size="-small"
                    @click="handleCopy(data.item.printer_id)">
            {{$t('printers.modal.choosePrinter.copy')}}
          </b-button>
          <b-button v-if="action === 'print' && canStartQueue(data.item)"
                    :disabled="processing || isDisabledQueue(data.item.flags)"
                    variant="-primary"
                    size="-small"
                    @click="handleStartQueue(data.item.printer_id)">
            {{$t('printers.modal.choosePrinter.queue')}}
          </b-button>
          <b-button v-if="action === 'restart' && canRestartQueue(data.item)"
                    :disabled="processing || isDisabledQueue(data.item.flags)"
                    variant="-primary"
                    size="-small"
                    @click="handleRestart(data.item.printer_id, true)">
            {{$t('printers.modal.choosePrinter.queue')}}
          </b-button>
        </template>
      </b-table>
    </div>
    <div class="label" v-if="showOriginalJobOwner">
      <p>Who will own new jobs?</p>
      <b-form-radio v-model="originalJobOwner" size="sm" value="yes">Original owner</b-form-radio>
      <b-form-radio v-model="originalJobOwner" size="sm" value="no">Me</b-form-radio>
    </div>
    <div class="label" v-if="showSharePrinter">
      <b-checkbox v-model="sharePrinter">{{$t('printers.modal.choosePrinter.shareLabel')}}</b-checkbox>
    </div>
  </div>
</template>

<script>
  import {mapGetters, mapActions} from 'vuex';
  import File from '@/mixins/file'
  import Printer from '@/mixins/printer'
  import Modal from '@/mixins/modal'
  import JobCost from '../job/JobCost';
  import JobFilament from '../job/JobFilament';
  import PrinterStatus from '../printer/PrinterStatus';
  import PrinterFilamentLabels from '../printer/PrinterFilamentLabels';
  import PrinterInlineNote from "../printer/PrinterInlineNote";
  import HumanDuration from "../../HumanDuration";
  import ModelPreview from "../../ModelPreview";

  const generateFilamentsDataArray = function(data) {
    let filaments = [];
    let extruders = [];
    if (data.extruders) {
      extruders = JSON.parse(data.extruders);
    }
    if (extruders && extruders.length > 1) {
      for (let i in extruders) {
        const ex = extruders[i];
        filaments.push({
          weight: ex.w,
          material: ex.m ? ex.m : data.material_type
        });
      }
    } else {
      filaments.push({
        weight: data.filament !== undefined ? data.filament : data.weight,
        material: data.material_type
      })
    }

    return filaments;
  };

  const getPropertyValueIfEqual = function(list, property) {
    if (list.length > 1) {
      for (let i = 1; i < list.length; i++) {
        let curPropertyValue = typeof property === 'function' ? property(list[i]) : list[i][property];
        let prevPropertyValue = typeof property === 'function' ? property(list[i-1]) : list[i-1][property];
        if (curPropertyValue !== prevPropertyValue) {
          return undefined;
        }
      }
    }

    return typeof property === 'function' ? property(list[0]) : list[0][property];
  };

  export default {
    name: 'choose-printer-for-action',
    components: {
      ModelPreview,
      HumanDuration,
      PrinterInlineNote,
      JobCost,
      JobFilament,
      PrinterStatus,
      PrinterFilamentLabels
    },
    props: ['job', 'jobId', 'jobs', 'fileId', 'printerId', 'printerTypeId', 'printerType', 'onlyIndustrial', 'onlyQueue', 'action'],
    mixins: [File, Printer, Modal],
    data() {
      return {
        loaded: false,
        filesData: [],
        jobsData: [],
        originalJobOwner: 'yes',
        sharePrinter: true,
        processing: false,
        fields: [
          {
            key: 'printer', class: 'machine'
          },
          {
            key: 'filament',
          },
          {
            key: 'released',
          },
          {
            key: 'action1',
            class: 'action1'
          },
          {
            key: 'action2',
            class: 'action2'
          }
        ]
      }
    },
    created() {
      if (!this.isPrintersLoaded) {
        this.startListenPrinterEvents();
      }
      let fileIds = [];
      if (this.fileId && this.fileId instanceof Array) {
        fileIds = this.fileId;
      } else if (this.fileId) {
        fileIds.push(this.fileId);
      }
      if (this.jobId && !this.job) {
        this.fetchJobData(this.jobId).then(({data}) => {
          this.loaded = true;
          if (data.result) {
            this.jobsData.push(data.message);
          } else {
            this.msgBoxOk(this.$t('printers.modal.choosePrinter.errorLoadingJobData'))
              .then(() => {
                this.$emit('close');
              });
          }
        });
      } else if (this.job) {
        this.jobsData.push(this.job);
        this.loaded = true;
      } else if (this.jobs) {
        this.jobsData = this.jobs;
        this.loaded = true;
      } else if (fileIds.length) {
        this.filesData = [];
        let counter = 0;
        fileIds.forEach(fileId => {
          this.getPrintFileInfo(fileId).then(({data}) => {
            counter++;
            if (data.result) {
              this.filesData.push(Object.assign({}, data.message, {id: fileId}));
            }
            if (fileIds.length === counter) {
              this.loaded = true;
            }
          })
        })
      }
    },
    computed: {
      ...mapGetters(['isPrintersLoaded', 'getPrinters', 'getPrinterIndex', 'getActivePrinters', 'getPrintSettings', 'isPrinterHasNotFinishedJobs']),

      showOriginalJobOwner() {
        return false;//this.action === 'copy';
      },

      showSharePrinter() {
        return this.action === 'copy';
      },

      files() {
        let files = [];
        if (this.jobsData && this.jobsData.length) {
          this.jobsData.forEach(jobData => {
            if (this.action === 'move' && jobData.job_status_id != 11) {
              //Move only jobs in queue
              return;
            }
            files.push({
              id: jobData.file_id,
              filename: jobData.filename,
              cost: jobData.cost,
              filaments: generateFilamentsDataArray(jobData),
              printingDuration: jobData.duration,
              picturePreview: jobData.picture_preview
            })
          });
        } else if (this.filesData && this.filesData.length) {
          this.filesData.forEach(fileData => {
            files.push({
              id: fileData.id,
              filename: fileData.filename,
              cost: fileData.cost,
              filaments: generateFilamentsDataArray(fileData),
              printingDuration: fileData.printing_duration,
              picturePreview: fileData.picture_preview
            });
          });
        }
        return files;
      },

      currentPrinterId() {
        if (this.printerId) {
          return this.printerId;
        }
        if (this.jobsData && this.jobsData.length) {
          return getPropertyValueIfEqual(this.jobsData, 'printer_id');
        }

        return undefined;
      },

      currentPrinterTypeId() {
        if (this.printerTypeId) {
          return this.printerTypeId;
        }
        if (this.jobsData && this.jobsData.length) {
          return getPropertyValueIfEqual(this.jobsData, job => {
            const printerIndex = this.getPrinterIndex(job.printer_id);
            return this.getPrinters[printerIndex].printer_type_id;
          });
        }

        return undefined;
      },

      currentPrinterType() {
        if (this.printerType) {
          return this.printerType;
        }
        if (this.filesData && this.filesData.length) {
          return getPropertyValueIfEqual(this.filesData, 'printer_type');
        }

        return undefined;
      },

      isOnlyIndustrial() {
        if (this.onlyIndustrial) {
          return true;
        }
        if (!this.filesData || !this.filesData.length) {
          return false;
        }
        if (this.currentPrinterType === null) {
          return true;
        }
        for (let i in this.filesData) {
          if (this.filesData[i].file_type_id != 2) {
            //Not gcode
            return true;
          }
        }

        return false;
      },

      printersList() {
        let printers = this.getActivePrinters.filter(printer => {
          if (this.action === 'move' && printer.printer_id === this.currentPrinterId) {
            return false;
          }
          if (this.isOnlyIndustrial) {
            return printer.virtual === 't';
          }
          if (printer.printer_type_id == this.currentPrinterTypeId || printer.printer_type == this.currentPrinterType) {
            return true;
          }

          return this.action !== 'print' && printer.virtual === 't';
        });
        printers.sort((a, b) => {
          const aWeight = this.getPrinterSortWeight(a);
          const bWeight = this.getPrinterSortWeight(b);
          if (aWeight === bWeight) {
            if (a.printer_name === b.name) return 0;
            return a.name < b.name ? -1 : 1;
          }

          return aWeight < bWeight ? -1 : 1;
        });

        return printers;
      }
    },
    methods: {
      ...mapActions(['startListenPrinterEvents', 'copyJobToPrinter', 'moveJobToPrinter', 'fetchJobData', 'getPrintFileInfo', 'printFile']),

      getPrinterSortWeight(printer) {
        if (this.currentPrinterId && printer.printer_id == this.currentPrinterId) {
          return 0;
        }
        if (printer.printer_type_id == this.currentPrinterTypeId || printer.printer_type == this.currentPrinterType) {
          if (printer.virtual=='t') return 5;
          if (printer.state=='idle') return 1;
          if (printer.state!='offline') return 2;
          return 4;
        }
        if (printer.virtual=='t') return 6;

        return 10;
      },

      canRestartQueue(printer) {
        if (!this.jobsData || !this.jobsData.length) {
          return false;
        }
        return this.canAddToPrinterQueue(printer);
      },

      canRestartPrint(printer) {
        if (this.onlyQueue || !this.canRestartQueue(printer)) {
          return false;
        }

        return this.canPrintOnPrinter(printer);
      },

      canStartQueue(printer) {
        if (!this.filesData || !this.filesData.length) {
          return false;
        }
        return this.canAddToPrinterQueue(printer);
      },

      canStartPrint(printer) {
        if (this.onlyQueue || !this.canStartQueue(printer) || this.filesData.length > 1) {
          return false;
        }

        return this.canPrintOnPrinter(printer);
      },

      handleCopy(printerId) {
        this.processing = true;
        const request = {
          printerId: printerId,
          jobId: this.jobsData[0].job_id, //TODO: implement multiple jobs case
          sharePrinter: this.sharePrinter,
          originalPrinterOwner: this.originalPrinterOwner,
          onlyQueue: true
        };
        this.copyJobToPrinter(request).then(({data}) => {
          this.processing = false;
          if (data.result) {
            this.$emit('close');
          } else {
            this.toast(data.message, {variant: 'success'});
          }
        }).catch((e) => {
          this.processing = false;
          this.msgBoxOk(this.$t('printers.modal.choosePrinter.errorCopyJob'));
          console.log(e);
        });
      },
      handleMove(printerId) {
        this.processing = true;
        const request = {
          printerId: printerId,
          jobId: this.jobsData.filter(job => job.job_status_id == 11).map(job => job.job_id !== undefined ? job.job_id : job.id),
          sharePrinter: this.sharePrinter,
        };
        this.moveJobToPrinter(request).then(({data}) => {
          this.processing = false;
          if (data.result) {
            this.$emit('close');
          } else {
            this.toast(data.message, {variant: 'success'});
          }
        }).catch((e) => {
          this.processing = false;
          this.msgBoxOk(this.$t('printers.modal.choosePrinter.errorMoveJob'));
          console.log(e);
        });
      },

      handleStartQueue(printerId) {
        this.processing = true;
        this.sendNextFileToQueue(printerId, 0, [], (failedFilenames) => {
          this.processing = false;
          if (failedFilenames.length === this.files.length) {
            this.toast(this.$t('printers.modal.choosePrinter.errorQueueJob'), {variant: 'danger'});
          } else if (failedFilenames.length > 0) {
            this.toast(this.$t('printers.modal.choosePrinter.errorQueueMultipleJobs', {filenames: failedFilenames.join(', ')}), {variant: 'danger'});
          } else {
            this.toastHtml(this.$t('printers.modal.choosePrinter.successQueue') + ' <a href="/#/printers" onclick="closeToast(this)">'+this.$t('printers.modal.choosePrinter.goToPrinters')+'</a>', {
              variant: 'success'
            });
            this.$emit('close');
          }
        });
      },

      sendNextFileToQueue(printerId, fileIndex, failedFilenames, callback) {
        this.printFile({fileId: this.files[fileIndex].id, printerId: printerId, onlyQueue: true}).then(({data}) => {
          if (!data.result) {
            failedFilenames.push(this.files[fileIndex].filename);
          }
          if (fileIndex < this.files.length - 1) {
            this.sendNextFileToQueue(printerId, fileIndex + 1, failedFilenames, callback);
          } else {
            callback(failedFilenames);
          }
        });
      },

      handleStartPrint(printerId) {
        this.processing = true;
        this.printFile({fileId: this.filesData[0].id, printerId: printerId, onlyQueue: false}).then(({data}) => {
          this.processing = false;
          if (data.result) {
            this.toastHtml(this.$t('printers.modal.choosePrinter.successPrint') + ' <a href="/#/printers" onclick="closeToast(this)">'+this.$t('printers.modal.choosePrinter.goToPrinters')+'</a>', {
              variant: 'success'
            });
            this.$emit('close');
          } else {
            this.toast(data.message, {variant: 'danger'});
          }
        });
      },

      handleRestart(printerId, queue) {
        this.processing = true;
        const request = {
          printerId: printerId,
          jobId: this.jobsData[0].job_id,
          sharePrinter: this.sharePrinter,
          originalPrinterOwner: this.originalPrinterOwner,
          onlyQueue: queue
        };
        this.copyJobToPrinter(request).then(({data}) => {
          this.processing = false;
          if (data.result) {
            this.$emit('close');
          } else {
            this.toast(data.message, {variant: 'danger'});
          }
        }).catch((e) => {
          this.processing = false;
          this.msgBoxOk(this.$t('printers.modal.choosePrinter.errorRestartJob'));
          console.log(e);
        });
      }
    }
  }
</script>

<style scoped lang="scss">
  .label {
    span {
      margin-right: 10px;
    }
  }
  .cell-printer-name {
    span {
      margin-right: 5px;
    }
  }
</style>

<style lang="scss">
  .choose-printer {
    margin: 0;
    padding: 0;

    &__scroll {
      max-height: 75vh;
      overflow: scroll;
    }

    table {
      margin: 0;
      thead {
        display: none;
      }
      tr {
        padding: 0 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .machine {
          width: 40%;
        }
      }
    }

    &__file {
      font-size: 16px;
      line-height: 1;
      padding: 24px 16px 12px;
      display: flex;
      align-items: center;
      h3 {
        font-size: 16px;
        margin: 0;
      }
      > * {
        margin-left: 18px;
      }
      a {
        margin-left: 0;
        margin-right: 5px;
      }
    }
  }
</style>
